import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ContactFormFields = ({ 
  formData = {},
  onFieldChange = () => {},
  errors = {},
  className = '' 
}) => {
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const handleInputChange = (field, value) => {
    onFieldChange(field, value);
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value?.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (cleaned?.length >= 10) {
      return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3, 6)}-${cleaned?.slice(6, 10)}`;
    } else if (cleaned?.length >= 6) {
      return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3, 6)}-${cleaned?.slice(6)}`;
    } else if (cleaned?.length >= 3) {
      return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3)}`;
    }
    return cleaned;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e?.target?.value);
    handleInputChange('phone', formatted);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Essential Fields */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center">
          <Icon name="User" size={20} className="mr-2" />
          Basic Information
        </h3>

        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter first name"
            value={formData?.firstName || ''}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter last name"
            value={formData?.lastName || ''}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
          />
        </div>

        {/* Phone */}
        <Input
          label="Phone Number"
          type="tel"
          placeholder="(555) 123-4567"
          value={formData?.phone || ''}
          onChange={handlePhoneChange}
          error={errors?.phone}
          required
        />

        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          placeholder="john.doe@example.com"
          value={formData?.email || ''}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
        />

        {/* Birthday */}
        <Input
          label="Birthday"
          type="date"
          value={formData?.birthday || ''}
          onChange={(e) => handleInputChange('birthday', e?.target?.value)}
          error={errors?.birthday}
          description="Used for birthday reminders"
        />
      </div>
      {/* Toggle Additional Fields */}
      <div className="border-t border-border pt-4">
        <Button
          variant="ghost"
          onClick={() => setShowAdditionalFields(!showAdditionalFields)}
          iconName={showAdditionalFields ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          className="w-full justify-between"
        >
          Additional Information
        </Button>
      </div>
      {/* Additional Fields */}
      {showAdditionalFields && (
        <div className="space-y-4 animate-slide-up">
          <h3 className="text-lg font-semibold text-text-primary flex items-center">
            <Icon name="MapPin" size={20} className="mr-2" />
            Additional Details
          </h3>

          {/* Company */}
          <Input
            label="Company"
            type="text"
            placeholder="Company name"
            value={formData?.company || ''}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            error={errors?.company}
          />

          {/* Job Title */}
          <Input
            label="Job Title"
            type="text"
            placeholder="Position or role"
            value={formData?.jobTitle || ''}
            onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
            error={errors?.jobTitle}
          />

          {/* Address */}
          <div className="space-y-3">
            <Input
              label="Street Address"
              type="text"
              placeholder="123 Main Street"
              value={formData?.address || ''}
              onChange={(e) => handleInputChange('address', e?.target?.value)}
              error={errors?.address}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="City"
                type="text"
                placeholder="City"
                value={formData?.city || ''}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                error={errors?.city}
              />
              <Input
                label="State"
                type="text"
                placeholder="State"
                value={formData?.state || ''}
                onChange={(e) => handleInputChange('state', e?.target?.value)}
                error={errors?.state}
              />
              <Input
                label="ZIP Code"
                type="text"
                placeholder="12345"
                value={formData?.zipCode || ''}
                onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                error={errors?.zipCode}
              />
            </div>
          </div>

          {/* Website */}
          <Input
            label="Website"
            type="url"
            placeholder="https://example.com"
            value={formData?.website || ''}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
            error={errors?.website}
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notes
            </label>
            <textarea
              placeholder="Add any additional notes about this contact..."
              value={formData?.notes || ''}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            {errors?.notes && (
              <p className="text-error text-sm mt-1">{errors?.notes}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactFormFields;